import { useCallback, useEffect, useId, useState } from 'react';
import {
  memberSearchApiV1InternalUsersIdSearchesSearchIdGet,
  rerunMemberSearchApiV1InternalUsersIdSearchesSearchIdRerunPost,
  type AdminMemberSearch,
  type AdminMemberSearchDetail,
  type AdminMemberSearchRerun,
  type AdminRecentSearch,
  type AdminSearchResult,
} from '@closet-theory/api';
import { Link } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { LoadingIndicator } from '@ui/components/application/loading-indicator/loading-indicator';
import { CheckboxField } from '@ui/console/forms';
import { formatRelative, formatTimestamp, humanize } from '@ui/console/format';
import { Callout, FailureText } from '@ui/console/primitives';
import { cx } from '@ui/utils/cx';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Filters } from './components';
import { GarmentImage } from './garment-image';
import { errorDetail } from './operation-policy';
import { refHref } from './references';

export function SearchHistoryFilters({
  foundNothing,
  onFoundNothingChange,
}: {
  foundNothing: boolean;
  onFoundNothingChange: (foundNothing: boolean) => void;
}) {
  return (
    <Filters>
      <CheckboxField
        label="Found nothing only"
        checked={foundNothing}
        onChange={(event) => onFoundNothingChange(event.currentTarget.checked)}
      />
    </Filters>
  );
}

function filterValue(value: unknown): string {
  if (typeof value === 'string') return humanize(value);
  if (value === null) return 'None';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    // Circular or otherwise unserialisable; say so rather than print "[object Object]".
    return 'Unreadable value';
  }
}

type SearchEntry = AdminMemberSearch | AdminRecentSearch;

/** The day result capture started; searches settled before it kept no result ids. */
const RESULTS_RECORDED_FROM = '2026-09-29';
const SMART_RERUN_UNSUPPORTED = 'smart_search_rerun_unsupported';
const SMART_RERUN_NOTE =
  "Re-running AI searches isn't available because it would spend the member's AI quota.";

type Load<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'failed'; detail: string }
  | { status: 'ready'; data: T };

type Outcome<T> = { data?: T; error?: unknown; response?: Response };

/** One request to a settled state; a smart-search refusal comes back as `'smart'`. */
async function settle<T>(request: () => Promise<Outcome<T>>): Promise<Load<T> | 'smart'> {
  try {
    configureAdminApi();
    const result = await request();
    const status = result.response?.status;
    handleUnauthorized(status);
    if (status === 403) return { status: 'forbidden' };
    if (result.error || !result.data) {
      const detail = errorDetail(result.error, 'request_failed');
      if (status === 422 && detail === SMART_RERUN_UNSUPPORTED) return 'smart';
      return { status: 'failed', detail };
    }
    return { status: 'ready', data: result.data };
  } catch (cause) {
    return {
      status: 'failed',
      detail: cause instanceof Error && cause.message ? cause.message : 'request_failed',
    };
  }
}

function resultNoun(kind: AdminSearchResult['kind']): string {
  if (kind === 'outfit') return 'outfit';
  if (kind === 'product') return 'product';
  return 'garment';
}

function countLabel(count: number, kind: AdminSearchResult['kind']): string {
  const noun = resultNoun(kind);
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

/**
 * Results in the order given. A garment or outfit still in the wardrobe links
 * to its detail on the member's page; one that is archived, hidden or deleted
 * is dimmed, and a shop product (never resolved against the catalogue) says so.
 */
function ResultGrid({
  results,
  memberId,
  label,
}: {
  results: AdminSearchResult[];
  memberId: string;
  label: string;
}) {
  return (
    <ol aria-label={label} className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
      {results.map((result, index) => {
        const noun = resultNoun(result.kind);
        const title =
          result.title?.trim() ||
          (result.category ? humanize(result.category) : `Untitled ${noun}`);
        const product = result.kind === 'product' || result.available === null;
        const unavailable = !product && result.available === false;
        const href =
          product || unavailable
            ? undefined
            : refHref({
                kind: result.kind === 'outfit' ? 'outfit' : 'garment',
                id: result.id,
                memberId,
              });
        return (
          <li
            key={`${result.id}-${index}`}
            data-result-state={product ? 'product' : unavailable ? 'unavailable' : 'available'}
            className={cx('flex min-w-0 flex-col gap-1.5', unavailable && 'opacity-50')}
          >
            <GarmentImage url={result.thumbnail_url} alt={title} />
            {href ? (
              <Link
                to={href}
                className="truncate text-sm font-medium text-primary underline decoration-dotted underline-offset-2 outline-brand hover:text-brand-secondary focus-visible:outline-2"
              >
                {title}
              </Link>
            ) : (
              <span className="truncate text-sm font-medium text-primary">{title}</span>
            )}
            {result.category && result.title?.trim() ? (
              <span className="truncate text-xs text-tertiary">{humanize(result.category)}</span>
            ) : null}
            {product ? (
              <span className="text-xs text-tertiary">Product (not in catalogue)</span>
            ) : unavailable ? (
              <span className="text-xs font-medium text-tertiary">Unavailable now</span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function Pending() {
  return (
    <div className="flex items-center gap-2 py-4 text-sm text-tertiary" aria-live="polite">
      <LoadingIndicator size="sm" type="dot-circle" />
      Loading…
    </div>
  );
}

function LoadProblem<T>({
  load,
  forbidden,
  failed,
  onRetry,
}: {
  load: Load<T>;
  forbidden: string;
  failed: string;
  onRetry: () => void;
}) {
  if (load.status === 'forbidden')
    return (
      <Callout tone="warning" title={forbidden}>
        This is a permission boundary, not an empty result. Ask an admin to widen your access.
      </Callout>
    );
  if (load.status === 'failed')
    return (
      <Callout
        tone="error"
        title={failed}
        actions={
          <Button size="sm" color="secondary" onClick={onRetry}>
            Try again
          </Button>
        }
      >
        <FailureText raw={load.detail} />
      </Callout>
    );
  return null;
}

function SearchResults({ search, memberId }: { search: SearchEntry; memberId: string }) {
  const recordedIds = search.result_ids ?? [];
  const recorded = recordedIds.length > 0;
  const [smart, setSmart] = useState(search.surface === 'smart_search');
  const [seen, setSeen] = useState<Load<AdminMemberSearchDetail>>({ status: 'idle' });
  const [rerun, setRerun] = useState<Load<AdminMemberSearchRerun>>({ status: 'idle' });

  const loadSeen = useCallback(async () => {
    setSeen({ status: 'loading' });
    const outcome = await settle(() =>
      memberSearchApiV1InternalUsersIdSearchesSearchIdGet({
        path: { id: memberId, search_id: search.id },
      }),
    );
    setSeen(outcome === 'smart' ? { status: 'failed', detail: SMART_RERUN_UNSUPPORTED } : outcome);
  }, [memberId, search.id]);

  useEffect(() => {
    if (recorded) void loadSeen();
  }, [recorded, loadSeen]);

  const runNow = async () => {
    setRerun({ status: 'loading' });
    const outcome = await settle(() =>
      rerunMemberSearchApiV1InternalUsersIdSearchesSearchIdRerunPost({
        path: { id: memberId, search_id: search.id },
      }),
    );
    if (outcome === 'smart') {
      setSmart(true);
      setRerun({ status: 'idle' });
      return;
    }
    setRerun(outcome);
  };

  return (
    <div className="flex flex-col gap-4 border-t border-secondary pt-3">
      {recorded ? (
        <section className="flex flex-col gap-2" aria-label="What the member saw">
          <h4 className="text-sm font-semibold text-primary">What the member saw</h4>
          {seen.status === 'idle' || seen.status === 'loading' ? <Pending /> : null}
          <LoadProblem
            load={seen}
            forbidden="Your role cannot read what this member saw."
            failed="The results this member saw could not be loaded."
            onRetry={() => void loadSeen()}
          />
          {seen.status === 'ready' ? (
            <>
              <p className="text-xs text-tertiary">
                In the order the member saw them, as they stand now.
                {search.result_count > recordedIds.length
                  ? ` Showing the first ${recordedIds.length} of ${search.result_count}.`
                  : ''}
              </p>
              {seen.data.results.length ? (
                <ResultGrid
                  results={seen.data.results}
                  memberId={memberId}
                  label="Results the member saw"
                />
              ) : (
                <p className="text-sm text-tertiary">None of these results could be found now.</p>
              )}
            </>
          ) : null}
        </section>
      ) : (
        <p className="text-sm text-tertiary">
          Results weren&apos;t recorded for this search (before {RESULTS_RECORDED_FROM}).
        </p>
      )}

      {smart ? (
        <p className="text-sm text-tertiary">{SMART_RERUN_NOTE}</p>
      ) : rerun.status === 'idle' ? (
        <div>
          <Button
            size="sm"
            color={recorded ? 'secondary' : 'primary'}
            onClick={() => void runNow()}
          >
            Run this search now
          </Button>
        </div>
      ) : (
        <section className="flex flex-col gap-2" aria-label="Results if searched now">
          {rerun.status === 'loading' ? <Pending /> : null}
          <LoadProblem
            load={rerun}
            forbidden="Your role cannot re-run this member's searches."
            failed="This search could not be re-run."
            onRetry={() => void runNow()}
          />
          {rerun.status === 'ready' ? (
            <>
              <h4 className="text-sm font-semibold text-primary">
                Results if searched now (run at {formatTimestamp(rerun.data.ran_at)})
              </h4>
              <p className="text-xs text-tertiary">
                A re-run against the member&apos;s wardrobe today, not what the member saw. Nothing
                was recorded. Found {countLabel(rerun.data.result_count, rerun.data.result_kind)}.
              </p>
              {rerun.data.results.length ? (
                <ResultGrid
                  results={rerun.data.results}
                  memberId={memberId}
                  label="Results if searched now"
                />
              ) : (
                <p className="text-sm text-tertiary">This search finds nothing now.</p>
              )}
              <div>
                <Button size="sm" color="secondary" onClick={() => void runNow()}>
                  Run again
                </Button>
              </div>
            </>
          ) : null}
        </section>
      )}
    </div>
  );
}

function SearchResultsDisclosure({ search, memberId }: { search: SearchEntry; memberId: string }) {
  const [open, setOpen] = useState(false);
  const panel = useId();
  return (
    <div className="flex flex-col gap-3">
      <div>
        <Button
          size="sm"
          color="tertiary"
          aria-expanded={open}
          aria-controls={panel}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? 'Hide results' : 'Show results'}
        </Button>
      </div>
      {open ? (
        <div id={panel}>
          <SearchResults search={search} memberId={memberId} />
        </div>
      ) : null}
    </div>
  );
}

export function SearchHistoryList({
  searches,
  memberId,
}: {
  searches: SearchEntry[];
  /** Whose searches these are, for lists whose entries don't carry `member_id`. */
  memberId?: string;
}) {
  const newestFirst = [...searches].sort(
    (left, right) => new Date(right.last_seen_at).getTime() - new Date(left.last_seen_at).getTime(),
  );
  return (
    <ol className="flex flex-col gap-3" aria-label="Settled searches">
      {newestFirst.map((search) => {
        const filterEntries = Object.entries(search.filters);
        const foundNothing = search.result_count === 0;
        const owner = 'member_id' in search ? search.member_id : memberId;
        return (
          <li
            key={search.id}
            data-search-result={foundNothing ? 'found-nothing' : 'has-results'}
            className={cx(
              'flex flex-col gap-3 rounded-xl border bg-primary p-4',
              foundNothing
                ? 'border-error-secondary ring-1 ring-error-secondary'
                : 'border-secondary',
            )}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                {'member_id' in search ? (
                  <Link
                    to={`/consumers/${search.member_id}`}
                    className="text-sm font-medium text-brand-secondary outline-brand focus-visible:outline-2"
                  >
                    {search.member_name}
                  </Link>
                ) : null}
                <h3 className="break-words text-lg font-semibold text-primary">{search.query}</h3>
              </div>
              <span
                className={cx(
                  'shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold',
                  foundNothing
                    ? 'bg-error-primary text-error-primary'
                    : 'bg-success-primary text-success-primary',
                )}
              >
                {foundNothing
                  ? 'Found nothing · 0 results'
                  : `${search.result_count} ${search.result_count === 1 ? 'result' : 'results'}`}
              </span>
            </div>

            {search.occurrences > 1 ? (
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                <span className="text-tertiary">seen {search.occurrences} times</span>
              </div>
            ) : null}

            {search.view || filterEntries.length ? (
              <dl className="flex flex-wrap gap-2 text-xs">
                {search.view ? (
                  <div className="rounded-md bg-secondary px-2 py-1 text-secondary">
                    <dt className="inline text-tertiary">View: </dt>
                    <dd className="inline font-medium">{humanize(search.view)}</dd>
                  </div>
                ) : null}
                {filterEntries.map(([key, value]) => (
                  <div key={key} className="rounded-md bg-secondary px-2 py-1 text-secondary">
                    <dt className="inline text-tertiary">{humanize(key)}: </dt>
                    <dd className="inline font-medium">{filterValue(value)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-tertiary">
              <div>
                <dt className="inline">First seen </dt>
                <dd className="inline" title={formatTimestamp(search.first_seen_at)}>
                  {formatRelative(search.first_seen_at)}
                </dd>
              </div>
              <div>
                <dt className="inline">Last seen </dt>
                <dd className="inline" title={formatTimestamp(search.last_seen_at)}>
                  {formatRelative(search.last_seen_at)}
                </dd>
              </div>
            </dl>

            {owner ? <SearchResultsDisclosure search={search} memberId={owner} /> : null}
          </li>
        );
      })}
    </ol>
  );
}
